import { Celebration, Piggington, characterName } from "../components/Common.jsx";
import { effectiveAge } from "../lib/age.js";
import { equippedCharacterBaseId, wornPiggingtonCosmetics } from "../lib/oinks.js";
import {
  PM,
  ZONES,
  createRun,
  distanceOf,
  goalDone,
  goalProgress,
  jump,
  modeForAge,
  pmBestKeyFor,
  settle,
  spendBoost,
  steerBy,
  step,
  throwBall,
} from "../lib/pigMountain.js";
import { PM_SPRITE_SRC, backWearables, loadPmArt, pmPiece } from "../lib/pigMountainArt.js";
import { drawScene, metresPerPixel } from "../lib/pigMountainDraw.js";
import { mulberry32 } from "../lib/snackCatch.js";
const { useState, useEffect, useRef } = React;

/* --------------------------- Piggington's Arcade — Pig Mountain (LB-256) --------------------------- */
//
// The arcade's second game: a sled run seen from behind and above. Distance is the score and a
// run ends when the sled stops on a hill. docs/PIG_MOUNTAIN_SCOPE.md is the doc of record; the
// rules live in lib/pigMountain.js and the painting in lib/pigMountainDraw.js. This file is the
// screens, the input and the loop that joins them.
//
// Law 1 posture, enforced structurally (same as SnackCatch.jsx): nothing in this file imports
// addTransaction, Oinks, OP, or any earning/request path. The score is metres, no currency
// imagery appears on any game screen, and the game writes NOTHING to household state — its
// only persistence is one device-local localStorage key per child (the best distance). That
// zero-write rule is what makes it genuinely offline-safe, so keep it absolute.
//
// Rendering: the slope is a <canvas>, the first in the app (Snack Attack moves DOM nodes). The
// child's character is NOT on the canvas — it is DOM (<PmRider>: sled, their character from
// behind, their cosmetics), positioned over the canvas every frame through a ref. The
// top row, banner, pop-up line and the two buttons are ordinary elements; React state for them
// is refreshed ten times a second, never per frame.
//
// Copy: the arcade keeps its own voice and these files are outside the copy-voice lint. Every
// string below was reviewed line by line on 2 Oct 2026, spaced hyphens and exclamation marks
// included — change wording only with a fresh review.

/* ------------------------------ the words ------------------------------ */

const fmtM = (n) => `${Math.max(0, Math.floor(n)).toLocaleString("en-GB")} m`;

const GOAL_TEXT = {
  spins: "Do 3 spins",
  wins: "Beat the Greedy Crow",
  near: "Make 5 near misses",
  airs: "Catch air off 6 crests",
  hats: "Knock off the Greedy Crow's hat",
};

const ZONE_NAME = Object.fromEntries(ZONES.map((z) => [z.id, z.name]));

// One line per thing lib/pigMountain.js can report. A function where the line carries a value.
const SAY = {
  goal: (v) => `Goal: ${GOAL_TEXT[v.goal]}`,
  zone: (v) => ZONE_NAME[v.zone],
  bump: "Bump - Ouch",
  nearMiss: "That was close!",
  spinLanded: "Nailed the landing",
  spinChain: (v) => `${v.n} in a row`,
  boostStored: "Boost saved for later",
  snowball: "Picked up snowballs to throw at Greedy Crow",
  noBalls: "Pick up snowballs first",
  saveBalls: "Save them for the Greedy Crow",
  raceStartBalls: "Press Throw to hit the Greedy Crow",
  raceStartNone: "No snowballs - race him to the line",
  crowHit: "Bullseye! Got him",
  missedGate: "Missed the gate",
  raceWon: (v) => `You beat the Greedy Crow! ${v.m} m added`,
  raceLost: "The Greedy Crow got there first",
  raidBalls: "Greedy Crow is dropping things! Throw snowballs at him",
  raidNone: "Look out, the Greedy Crow is dropping things",
  hatKnocked: "You knocked his hat off, catch it",
  hatCaught: (v) => `You caught his hat, ${v.m} m added`,
  tinhat: "Tin hat on - it protects you from one bump",
  shieldSaved: "The tin hat saved you",
  rocket: "Rocket sled",
  giant: "Giant snowball - you're unstoppable",
  squished: "Doh! You got squished.",
  boulderOutrun: "You outran it",
  boulderSmashed: "You smashed it",
  crevasse: "Oops, you fell into the crack",
  recordPassed: "Congratulations - new personal record!",
  siblingPassed: (v) => `Congratulations - you're beating ${v.name}`,
};

function lineFor(ev) {
  const s = SAY[ev.id];
  return typeof s === "function" ? s(ev.vars) : s || null;
}

/* ------------------------------ helpers ------------------------------ */

export function readPmBest(childId) {
  try {
    const raw = localStorage.getItem(pmBestKeyFor(childId));
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    return typeof parsed?.best === "number" && parsed.best > 0 ? Math.floor(parsed.best) : 0;
  } catch {
    return 0;
  }
}

function writePmBest(childId, best) {
  try { localStorage.setItem(pmBestKeyFor(childId), JSON.stringify({ best, at: Date.now() })); } catch {}
}

const NAVY = "#1B2A4A";
const MARIGOLD = "#FFC629";

function Dots({ n, of = 3, on, size = 10 }) {
  return (
    <span className="flex gap-[5px]" aria-hidden="true">
      {Array.from({ length: of }, (_, i) => (
        <span key={i} className="rounded-full box-border" style={{ width: size, height: size, border: `2px solid ${NAVY}`, background: i < n ? on : "transparent" }} />
      ))}
    </span>
  );
}

/* ------------------------------ the game ------------------------------ */

// Widest the playing area is allowed to get, for the same reason as Snack Attack's clamp
// (W1.14): on a tablet a full-bleed slope is wider than a thumb can cover.
const MAX_PLAYFIELD_W = 520;

function initialHud(R) {
  return { dist: 0, speed: 0, red: false, balls: R ? R.balls : 0, boosts: R ? R.boosts : 0, canThrow: false, shield: false, goal: "", banner: null, expr: "fx-happy", pose: "", sled: "dd-sled-level", flame: 0 };
}

function bannerFor(R) {
  if (R.race) {
    const lead = Math.round(R.z - R.race.crowZ);
    return {
      kind: "race",
      title: `Race ${R.race.n} against the Greedy Crow`,
      prize: `Win ${PM.RACE_BONUS} m`,
      left: `Gate ${Math.min(6, R.race.seen + 1)} of 6`,
      right: R.race.lost ? "The Greedy Crow has won" : lead >= 0 ? `You lead by ${lead} m` : `Greedy Crow leads by ${-lead} m`,
    };
  }
  if (R.boulder) return { kind: "boulder", title: "Giant snowball behind you", prize: `${Math.max(0, Math.round(R.z - R.boulder.z))} m` };
  if (R.gapWarn) return { kind: "gap", title: "Gap coming up - speed up to make the jump!" };
  if (R.warn) return { kind: "hill", title: "Hill coming up - you need more speed!" };
  return null;
}

function hudFor(R) {
  const done = goalDone(R);
  return {
    dist: distanceOf(R),
    speed: Math.round((R.v / PM.VMAX) * 100),
    red: R.warn || R.gapWarn,
    balls: R.balls,
    boosts: R.boosts,
    canThrow: R.balls > 0 && ((R.race && !R.race.lost) || (R.raid && !R.raid.hit)),
    shield: R.shield,
    goal: done ? "Goal done" : `${GOAL_TEXT[R.goal.k]}, ${goalProgress(R)} of ${R.goal.n}`,
    banner: bannerFor(R),
    expr: R.crash > 0 ? "fx-oops" : R.air ? "fx-wow" : "fx-happy",
    // The back-view rider's pose and sled (pigMountainArt.js): tumbling after a bump, the
    // throwing arm while a snowball is in the air, nose up in the air, the rocket sled and its
    // two flame frames while the rocket burns.
    pose: R.crash > 0 ? "-tumble" : R.shot ? "-throw" : "",
    sled: R.mode === "rocket" ? "dd-sled-rocket" : R.air ? "dd-sled-up" : "dd-sled-level",
    flame: R.mode === "rocket" ? 1 + (Math.floor(Date.now() / 100) & 1) : 0,
  };
}

// The ready screen's rows. `box` crops the symbol to the part worth showing at icon size.
const HOWTO = [
  { lead: "Steer", art: "dd-sled-level", size: [200, 260], box: "0 196 200 70", lines: ["Drag left and right to steer."] },
  { lead: "Jump and spin", art: "dd-rock-1", size: [130, 90], box: "-10 -20 150 130", lines: ["Flick up to jump over rocks and logs.", "Ramps and bumps send you flying. Flick again in the air to spin, then land it to go faster."] },
  { lead: "Go faster", art: "dd-boost-can", size: [90, 90], box: "-8 -8 106 106", lines: ["Yellow arrows make you faster.", "Yellow cans are boosts. Save them for the hills."] },
  { lead: "Greedy Crow", art: "cw-back-fly-2", size: [200, 260], box: "0 10 200 200", lines: ["Pick up snowballs, then press Throw when the Greedy Crow turns up."] },
];

// The child's character from behind, on the sled, in what they are wearing. One SVG in the
// shared 200 x 260 character space: sled, character, cosmetics ascending by z, then the rocket
// flame and the tin hat. width/height on every <use>, as <Piggington> does.
function PmRider({ base, cosmetics, pose, sled, flame, shield }) {
  const u = (id, key, extra) => <use key={key || id} href={`${PM_SPRITE_SRC}#${id}`} width="200" height="260" {...extra} />;
  return (
    <svg viewBox="0 0 200 260" className="w-full h-full overflow-visible" role="img" aria-label={`${characterName(base)} on a sled`}>
      {u(sled, "sled")}
      {u(`${base}-back${pose}`, "body")}
      {backWearables(cosmetics).map((id) => u(id, id, { "data-testid": `pm-wearing-${id}` }))}
      {flame > 0 && u(`dd-sled-flame-${flame}`, "flame")}
      {shield && <use data-testid="pm-shield" href={`${PM_SPRITE_SRC}#dd-tin-hat`} x="52" y="-22" width="96" height="96" />}
    </svg>
  );
}

function GameScreen({ child, siblings, pmTest, onQuit }) {
  const fieldRef = useRef(null);
  const canvasRef = useRef(null);
  const riderRef = useRef(null);
  const runRef = useRef(null);
  const viewRef = useRef({ W: 390, H: 844 });
  const gestRef = useRef(null);
  const toastUntilRef = useRef(0);
  const hudKeyRef = useRef("");
  const [screen, setScreen] = useState("playing");
  const screenRef = useRef("playing");
  screenRef.current = screen;
  const [runNo, setRunNo] = useState(0);
  const [hud, setHud] = useState(() => initialHud(null));
  const [toast, setToast] = useState(null);
  const [result, setResult] = useState(null);
  const worn = wornPiggingtonCosmetics(child);
  const characterBase = equippedCharacterBaseId(child);
  // The art loads once, in the background. Until it has (or if it cannot: no signal and no
  // cache) the run plays in placeholder shapes with the face-on character, exactly as before.
  const [artOk, setArtOk] = useState(() => !!pmPiece("cw-back-fly-1"));
  const artRef = useRef(artOk);
  artRef.current = artOk;
  useEffect(() => {
    let live = true;
    loadPmArt().then((ok) => { if (live && ok) setArtOk(true); });
    return () => { live = false; };
  }, []);

  // `stopped` is a run ended from the pause card rather than on a hill: same card, same best
  // rules, but it must not say the sled stopped on a hill.
  function finish(R, stopped) {
    const prev = readPmBest(child.id);
    const res = { ...settle(R, prev), stopped: !!stopped };
    if (res.isBest) writePmBest(child.id, res.dist);
    setResult(res);
    setScreen("gameover");
  }
  const finishRef = useRef(finish);
  finishRef.current = finish;

  // A new run: on mount and on every "Play again". The child's own best and each sibling's
  // best on this device become flags on the slope and lines when they are passed.
  useEffect(() => {
    const marks = [{ dist: readPmBest(child.id), mine: true, name: "" }];
    for (const s of siblings || []) marks.push({ dist: readPmBest(s.id), mine: false, name: s.name || "" });
    const R = createRun({
      mode: modeForAge(effectiveAge(child)),
      rng: pmTest ? mulberry32(1) : Math.random,
      marks,
      test: !!pmTest,
    });
    runRef.current = R;
    hudKeyRef.current = "";
    setHud(initialHud(R));
    setToast(null);
    setResult(null);
    setScreen("playing");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runNo, child.id]);

  // The loop. One requestAnimationFrame chain for the life of the screen: step the rules,
  // paint the slope, move the character, and (ten times a second) refresh the HUD state.
  useEffect(() => {
    let alive = true, raf = 0, last = 0, lastHud = 0;
    const canvas = canvasRef.current;
    let ctx = null;
    try { ctx = canvas && typeof canvas.getContext === "function" ? canvas.getContext("2d") : null; } catch { ctx = null; }

    function resize() {
      const el = fieldRef.current;
      if (!el || !canvas) return;
      const rect = el.getBoundingClientRect();
      const W = Math.max(200, Math.round(rect.width)), H = Math.max(300, Math.round(rect.height));
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      viewRef.current = { W, H, dpr };
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
    }
    resize();
    window.addEventListener("resize", resize);

    function frame(now) {
      if (!alive) return;
      const dt = Math.min(0.05, (now - last) / 1000 || 0);
      last = now;
      const R = runRef.current;
      if (R) {
        if (screenRef.current === "playing" && !R.over) {
          step(R, dt);
          if (R.events.length) {
            let said = null;
            for (const ev of R.events) { const line = lineFor(ev); if (line) said = line; }
            R.events.length = 0;
            if (said) { setToast(said); toastUntilRef.current = now + 1500; }
          }
          if (R.over) finishRef.current(R);
        }
        const view = viewRef.current;
        let out = null;
        if (ctx) {
          ctx.setTransform(view.dpr || 1, 0, 0, view.dpr || 1, 0, 0);
          out = drawScene(ctx, R, view, now, artRef.current ? pmPiece : null);
        }
        const rider = riderRef.current;
        if (rider && out) {
          // With the art, the box is the whole 200 x 260 stack (sled 176 units wide = 1.6 u) and
          // stands on the snow; without it, the face-on character sits on the canvas's sled.
          const art = artRef.current;
          const w = art ? out.u * 1.82 : (out.u * 1.9) / 1.3, h = w * 1.3;
          rider.style.display = out.giant ? "none" : "";
          rider.style.width = `${w}px`;
          rider.style.height = `${h}px`;
          rider.style.transform = `translate3d(${out.px - w / 2}px, ${out.py - h - (art ? -h * 0.012 : out.u * 0.3)}px, 0) rotate(${out.rot}rad)`;
        }
        if (now - lastHud > 100) {
          lastHud = now;
          const next = hudFor(R);
          const key = JSON.stringify(next);
          if (key !== hudKeyRef.current) { hudKeyRef.current = key; setHud(next); }
          if (toastUntilRef.current && now > toastUntilRef.current) { toastUntilRef.current = 0; setToast(null); }
        }
      }
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);

    function onKeyDown(e) {
      const R = runRef.current;
      if (!R || screenRef.current !== "playing") return;
      if (e.code === "ArrowLeft") R.keyL = true;
      else if (e.code === "ArrowRight") R.keyR = true;
      else if (e.code === "ArrowUp" || e.code === "Space") { e.preventDefault(); if (!e.repeat) jump(R); }
      else if (e.code === "KeyX") throwBall(R);
      else if (e.code === "KeyB") spendBoost(R);
    }
    function onKeyUp(e) {
      const R = runRef.current;
      if (!R) return;
      if (e.code === "ArrowLeft") R.keyL = false;
      if (e.code === "ArrowRight") R.keyR = false;
    }
    function onHidden() {
      if (document.hidden && screenRef.current === "playing") setScreen("paused");
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      document.removeEventListener("visibilitychange", onHidden);
    };
  }, []);

  // Touch: drag steers, a quick flick up jumps (or spins, in the air off a jump), a tap throws
  // while the Crow is about. The Throw and Boost buttons stop the event reaching here.
  function onPointerDown(e) {
    if (screen !== "playing") return;
    gestRef.current = { id: e.pointerId, x0: e.clientX, y0: e.clientY, lx: e.clientX, t0: performance.now(), flicked: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }
  function onPointerMove(e) {
    const g = gestRef.current, R = runRef.current;
    if (!g || !R || e.pointerId !== g.id || screen !== "playing") return;
    const dx = e.clientX - g.lx;
    g.lx = e.clientX;
    steerBy(R, dx * metresPerPixel(viewRef.current));
    const up = g.y0 - e.clientY, side = Math.abs(e.clientX - g.x0), quick = performance.now() - g.t0 < 320;
    if (!g.flicked && quick && up > 42 && up > side * 1.2) { g.flicked = true; jump(R); }
    else if (!g.flicked && quick && R.air && R.rampAir && !R.trick && side > 55) { g.flicked = true; jump(R); }
  }
  function onPointerUp(e) {
    const g = gestRef.current, R = runRef.current;
    gestRef.current = null;
    if (!g || !R || e.pointerId !== g.id) return;
    const tap = performance.now() - g.t0 < 260 && Math.abs(e.clientX - g.x0) < 12 && Math.abs(e.clientY - g.y0) < 12;
    if (tap && (R.race || R.raid)) throwBall(R);
  }
  function press(fn) {
    return (e) => {
      e.preventDefault();
      e.stopPropagation();
      const R = runRef.current;
      if (R && screen === "playing") fn(R);
    };
  }

  const b = hud.banner;
  return (
    <div className="w-full h-full relative select-none" style={{ touchAction: "none", color: NAVY }}>
      <div
        ref={fieldRef}
        data-testid="pm-field"
        className="absolute inset-0 overflow-hidden"
        style={{ background: "#CBE6F4" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => { gestRef.current = null; }}
      >
        <canvas ref={canvasRef} className="absolute left-0 top-0 w-full h-full block" aria-hidden="true" />
        {/* The child's own character, moved over the canvas by the loop: from behind, on the
            sled, once the art is in; face-on with the placeholder tin hat until then. */}
        <div ref={riderRef} data-testid="pm-rider" className="absolute left-0 top-0 will-change-transform pointer-events-none" style={{ width: 64, height: 83, transformOrigin: "50% 100%", transform: "translate3d(-200px, -200px, 0)" }}>
          {artOk ? (
            <PmRider base={characterBase} cosmetics={worn} pose={hud.pose} sled={hud.sled} flame={hud.flame} shield={hud.shield} />
          ) : (
            <>
              <Piggington expression={hud.expr} cosmetics={worn} base={characterBase} className="w-full h-full" />
              {hud.shield && (
                <svg data-testid="pm-shield" viewBox="0 0 60 30" className="absolute left-[14%] w-[72%]" style={{ top: "-4%" }} aria-hidden="true">
                  <path d="M10 26 A20 20 0 0 1 50 26 Z" fill="#A9B4C0" stroke={NAVY} strokeWidth="3" />
                  <rect x="2" y="23" width="56" height="6" rx="2" fill="#A9B4C0" stroke={NAVY} strokeWidth="3" />
                </svg>
              )}
            </>
          )}
        </div>
      </div>

      {/* Top row. The distance box grows with the number and the speed bar gives up the
          space, so the row keeps its height at any distance (Alan, 1 Oct). */}
      <div className="absolute top-0 inset-x-0 px-2.5 flex items-center gap-1.5 pointer-events-none" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 10px)" }}>
        <span data-testid="pm-distance" className="bg-white rounded-lg px-2.5 h-11 flex items-center text-xl font-extrabold whitespace-nowrap flex-none" style={{ fontVariantNumeric: "tabular-nums" }}>{fmtM(hud.dist)}</span>
        <span className="bg-white rounded-lg px-2.5 h-11 flex items-center gap-1.5 flex-1 min-w-[60px]" aria-label={`Speed ${hud.speed} percent`}>
          <span className="text-[13px] font-bold">Speed</span>
          <span className="flex-1 h-2.5 rounded-full bg-slate-200 overflow-hidden block">
            <span data-testid="pm-speed" className="block h-2.5 rounded-full" style={{ width: `${hud.speed}%`, background: hud.red ? "#C7354F" : NAVY, transition: "width 120ms linear" }} />
          </span>
        </span>
        <button data-testid="pm-pause" aria-label="Pause" onClick={() => { if (screen === "playing") setScreen("paused"); }} className="bg-white rounded-lg w-11 h-11 flex-none flex items-center justify-center gap-[5px] pointer-events-auto">
          <span className="block w-[5px] h-4 rounded-sm" style={{ background: NAVY }} /><span className="block w-[5px] h-4 rounded-sm" style={{ background: NAVY }} />
        </button>
      </div>

      {b && screen === "playing" && (
        <div data-testid="pm-banner" data-kind={b.kind} className="absolute inset-x-0 px-2.5 pointer-events-none" style={{ top: "calc(env(safe-area-inset-top, 0px) + 92px)" }}>
          <div className="rounded-lg px-3 py-2 text-white font-extrabold text-[15px] leading-5" style={{ background: NAVY }}>
            <div className="flex justify-between gap-2.5"><span>{b.title}</span>{b.prize && <span className="flex-none" style={{ color: MARIGOLD }}>{b.prize}</span>}</div>
            {b.left && <div className="flex justify-between gap-2.5 text-[13px] leading-[18px] font-bold mt-1"><span className="flex-none">{b.left}</span><span className="text-right">{b.right}</span></div>}
          </div>
        </div>
      )}

      {toast && screen === "playing" && (
        <div className="absolute inset-x-0 text-center pointer-events-none px-2.5" style={{ top: `calc(env(safe-area-inset-top, 0px) + ${b ? (b.left ? 150 : 128) : 92}px)` }}>
          <span data-testid="pm-toast" className="inline-block bg-white rounded-lg px-3 py-1.5 text-[14px] leading-5 font-extrabold max-w-full">{toast}</span>
        </div>
      )}

      {/* Throw (left) and Boost (right). The sled rides between them, so the run's goal sits under
          the distance box and every alert under that: nothing covers the course (Alan, 2 Oct). */}
      <button
        data-testid="pm-throw"
        aria-label={`Throw a snowball, ${hud.balls} held`}
        onPointerDown={press(throwBall)}
        className={`absolute left-3.5 w-[92px] h-[68px] rounded-lg flex flex-col items-center justify-center gap-1 text-[17px] font-extrabold ${hud.canThrow ? "pm-ready" : ""}`}
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 22px)", background: hud.balls ? "#FFFFFF" : "#E7EAF0", color: hud.balls ? NAVY : "#5B6577", touchAction: "none" }}
      >
        Throw
        <Dots n={hud.balls} on="#CFE3F0" size={12} />
      </button>
      <div data-testid="pm-goal" className="absolute bg-white rounded-lg px-2 py-1 text-[12px] leading-4 font-bold pointer-events-none" style={{ left: 10, top: "calc(env(safe-area-inset-top, 0px) + 62px)" }}>{hud.goal}</div>
      <button
        data-testid="pm-boost"
        aria-label={`Use a stored boost, ${hud.boosts} stored`}
        onPointerDown={press(spendBoost)}
        className="absolute right-3.5 w-[92px] h-[68px] rounded-lg flex flex-col items-center justify-center gap-1 text-[17px] font-extrabold"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 22px)", background: hud.boosts ? MARIGOLD : "#E7EAF0", color: hud.boosts ? NAVY : "#5B6577", touchAction: "none" }}
      >
        Boost
        <Dots n={hud.boosts} on={NAVY} />
      </button>

      {screen === "paused" && (
        <div data-testid="pm-paused" className="absolute inset-0 bg-black/60 flex items-center justify-center p-6">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-xs p-5 text-center">
            <div className="font-extrabold text-lg mb-3">Paused</div>
            <button data-testid="pm-resume" className="w-full font-extrabold rounded-lg h-12 text-base mb-2" style={{ background: MARIGOLD, color: NAVY }} onClick={() => setScreen("playing")}>Keep playing</button>
            <button data-testid="pm-stop" className="w-full bg-slate-100 font-bold rounded-lg h-12 text-base" onClick={() => { if (runRef.current) finish(runRef.current, true); }}>Stop this run</button>
          </div>
        </div>
      )}

      {/* Results. No balance, no currency, no "earn" language, ever. */}
      {screen === "gameover" && result && (
        <div data-testid="pm-gameover" className="absolute inset-0 bg-black/60 flex items-center justify-center p-6">
          {result.isBest && <Celebration variant="pig" count={20} />}
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-xs p-5 text-center">
            <div className="flex justify-center mb-1">
              <Piggington expression={result.isBest ? "fx-celebrating" : "fx-happy"} cosmetics={worn} base={characterBase} className="w-16 h-[83px]" />
            </div>
            {!result.stopped && <div data-testid="pm-over-title" className="font-extrabold text-lg">You stopped on the hill</div>}
            <div data-testid="pm-final" className="font-extrabold text-4xl my-1.5" style={{ fontVariantNumeric: "tabular-nums" }}>{fmtM(result.dist)}</div>
            {result.isBest ? (
              <div data-testid="pm-new-best" className="mb-2">
                <span className="inline-block font-extrabold text-sm rounded-lg px-3 py-1" style={{ background: MARIGOLD }}>That is a new best</span>
                {result.beatBy > 0 && <div className="text-[13px] font-bold text-slate-600 mt-1.5">You beat your old best by {fmtM(result.beatBy)}</div>}
              </div>
            ) : (
              <div data-testid="pm-best" className="text-[13px] font-bold text-slate-600 mb-2">Your best is {fmtM(result.best)}</div>
            )}
            <div data-testid="pm-goal-result" className="text-[13px] font-bold text-slate-600 mb-3">
              {result.goalDone ? `Goal done: ${GOAL_TEXT[result.goal.k]}` : `Goal: ${GOAL_TEXT[result.goal.k]}. You managed ${result.goalGot} of ${result.goal.n}.`}
            </div>
            <button data-testid="pm-again" className="w-full font-extrabold rounded-lg h-12 text-base mb-2" style={{ background: MARIGOLD, color: NAVY }} onClick={() => setRunNo((n) => n + 1)}>Play again</button>
            <button data-testid="pm-done" className="w-full bg-slate-100 font-bold rounded-lg h-12 text-base" onClick={onQuit}>Back to the arcade</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------ overlay shell ------------------------------ */

// Opened from the arcade picker (ArcadePicker.jsx), which has already dealt with the daily
// Brain Warm-Up. `pmTest` is the preview-mode-ONLY E2E hook (`&pmTest=1`): a fixed seed and a
// short slope straight into a long climb, so an untouched run ends in seconds at a known place.
export function PigMountainOverlay({ child, siblings, pmTest, onClose }) {
  const [screen, setScreen] = useState("ready");
  const best = readPmBest(child.id);
  return (
    <div data-testid="pig-mountain-overlay" className="fixed inset-0 bg-white z-50 flex flex-col" style={{ color: NAVY }}>
      <style>{`
        @keyframes pm-pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
        .pm-ready { animation: pm-pulse 0.7s ease-in-out infinite; outline: 4px solid ${MARIGOLD}; }
      `}</style>

      {screen === "ready" && (
        <div data-testid="pm-ready" className="bg-white w-full h-full flex flex-col items-center px-6 pb-6 relative overflow-y-auto" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 20px)" }}>
          <button aria-label="Close" className="absolute right-4 text-slate-400 text-xl font-bold px-1.5" style={{ top: "calc(env(safe-area-inset-top, 0px) + 16px)" }} onClick={onClose}>✕</button>
          <Piggington expression="fx-happy" cosmetics={wornPiggingtonCosmetics(child)} base={equippedCharacterBaseId(child)} className="w-20 h-[104px] mb-2 flex-none" />
          <div className="font-extrabold text-2xl mb-3">Pig Mountain</div>
          {/* How to play: four rows, each a picture from the game and a bold word to catch the eye
              (Alan, 2 Oct: the plain lines were a wall of text). The sentences are the ones he
              reviewed, unchanged. */}
          <div data-testid="pm-howto" className="w-full max-w-xs flex flex-col gap-2 mb-3">
            {HOWTO.map((row) => (
              <div key={row.lead} className="flex items-center gap-3 rounded-lg px-3 py-2.5" style={{ background: "#EEF5FA" }}>
                <svg viewBox={row.box} className="flex-none w-11 h-11" aria-hidden="true">
                  <use href={`${PM_SPRITE_SRC}#${row.art}`} width={row.size[0]} height={row.size[1]} />
                </svg>
                <div className="min-w-0">
                  <div className="font-extrabold text-[15px] leading-5">{row.lead}</div>
                  {row.lines.map((l) => <p key={l} className="text-[13px] leading-[18px] font-semibold text-slate-600">{l}</p>)}
                </div>
              </div>
            ))}
            <p className="text-[12px] leading-4 font-semibold text-slate-400 text-center">On a keyboard: left and right arrows steer, up arrow jumps, X throws, B boosts.</p>
          </div>
          {best > 0 && <div data-testid="pm-ready-best" className="text-[13px] font-bold text-slate-500 mb-2">Your best: {fmtM(best)}</div>}
          <button data-testid="pm-play" className="w-full max-w-xs font-extrabold rounded-lg h-12 text-base flex-none" style={{ background: MARIGOLD, color: NAVY }} onClick={() => setScreen("playing")}>Play</button>
        </div>
      )}

      {screen === "playing" && (
        <div className="w-full h-full flex justify-center" style={{ background: "#0f172a" }}>
          <div data-testid="pm-playfield-clamp" className="w-full h-full relative" style={{ maxWidth: MAX_PLAYFIELD_W }}>
            <GameScreen child={child} siblings={siblings} pmTest={pmTest} onQuit={onClose} />
          </div>
        </div>
      )}
    </div>
  );
}
